import { selectStandardProps } from "@/utils/componentUtils";
import { resolveFormValidationError, setErrorsToForm } from "@/utils/formUtils";
import { validations } from "@/utils/validationUtils";
import { Col, Form, Input, Modal, Row, Select } from "antd";
import { useTranslation } from "react-i18next";
import { useCreateVehicleModelMapping } from "../../../../../common/api/queries";
import { ModalSizes, rowGutter } from "../../../../../common/constants";
import { VehicleMappingType } from "../../enums";
import { CreateVehicleMappingFormItem, VehicleMapping, VehicleMappingData } from "../../types";
import { parseVehicleMappingsFromIdentifiers, VEHICLE_IDENTIFIER_DELIMITER, VEHICLE_TYPES_PATH } from "../../utils";

const colSpan = 6;

interface Props {
  open: boolean;
  selectedBrand?: VehicleMapping;
  selectedMappingTypes: VehicleMappingType[];
  mappingData: VehicleMappingData[];
  onFormCancel: () => void;
}

const VehicleModelCreateMappingModal = ({
  open,
  selectedBrand,
  selectedMappingTypes,
  mappingData,
  onFormCancel,
}: Props) => {
  const { t } = useTranslation();

  const [form] = Form.useForm<CreateVehicleMappingFormItem>();

  const createVehicleModelMapping = useCreateVehicleModelMapping();

  const handleFormSubmit = () => {
    if (selectedBrand) {
      form
        .validateFields()
        .then((values) => {
          createVehicleModelMapping.mutate(
            {
              id: selectedBrand.id,
              object: parseVehicleMappingsFromIdentifiers(values),
            },
            {
              onError: (error) => setErrorsToForm(form, "vehicle.attrs", error.violations),
              onSuccess: onFormCancel,
            },
          );
        })
        .catch(resolveFormValidationError);
    }
  };

  return (
    <Modal
      width={ModalSizes.HUGE}
      open={open}
      confirmLoading={createVehicleModelMapping.isPending}
      title={
        selectedBrand
          ? t("vehicle.titles.modelCreateForBrand", { brandName: selectedBrand.name })
          : t("vehicle.titles.modelCreate")
      }
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      afterClose={() => form.resetFields()}
      onOk={handleFormSubmit}
      onCancel={onFormCancel}
      mask={{ closable: false }}
    >
      <Form form={form} layout="vertical" name="vehicleModelCreateMappingForm">
        <Row gutter={rowGutter}>
          <Col span={colSpan}>
            <Form.Item name="name" label={t("vehicle.attrs.name")} rules={[validations.notBlank]}>
              <Input size="small" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={rowGutter}>
          {Object.values(VehicleMappingType).map((type) => {
            const typePath = VEHICLE_TYPES_PATH[type];
            const mapping = mappingData.find((mapping) => mapping.type === type);
            const brandMapping = mapping
              ? mapping.brands.find((brand) => selectedBrand && selectedBrand[typePath.keyId] === brand.id)
              : undefined;

            return selectedMappingTypes.includes(type) ? (
              <Col span={colSpan} key={type}>
                <Form.Item
                  name={typePath.keyIdentifier}
                  label={t(`vehicle.enums.vehicleMappingType.${type}`)}
                  rules={[validations.none]}
                >
                  <Select
                    {...selectStandardProps}
                    allowClear
                    size="small"
                    options={(brandMapping && brandMapping.models ? brandMapping.models : []).map((item) => ({
                      value: item.id + VEHICLE_IDENTIFIER_DELIMITER + item.name,
                      label: `${item.name} (${item.id})`,
                    }))}
                  />
                </Form.Item>
              </Col>
            ) : null;
          })}
        </Row>
      </Form>
    </Modal>
  );
};

export default VehicleModelCreateMappingModal;
